import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { currentLocale } from '../../i18n';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import SteamAvatar from '../common/SteamAvatar';
import SteamFlag from '../common/SteamFlag';
import AiChatPanel from '../chat/AiChatPanel';
import RankShield from '../elo/RankShield';
import { getRankTier } from '../elo/constants';
import { fetchCountriesBulk } from '../../utils/countries';
import { eloCache, fetchEloBulk, onEloLoaded } from '../../utils/eloBulk';
import { fetchAvatarsBulk } from '../../utils/avatars';
import {
  getMatchDraft,
  isInMatchDraft,
  subscribeMatchDraft,
  toggleMatchDraft,
} from '../../utils/matchDraft';
import './OnlineSidebar.css';

/**
 * Hämta presence-routingnyckeln för en användare i online-listan.
 *
 * Sprint 5 (player_id-cutover): backend (`presence.py`) använder
 * `presence_key = player_id || discord_id`. Sidebarens key, filter, chat-lookup
 * och DM-target måste alla bygga på samma fält — annars råkar `setActiveChat`
 * öppna en konversation som `loadHistory(my_id=...)` inte hittar och DM-routing
 * landar i fel Redis-channel.
 *
 * @param {object} u - User-objekt från online-listan ({ player_id?, discord_id, ... }).
 * @returns {string} player_id om finns, annars discord_id, annars tom sträng.
 */
const getKey = (u) => {
  if (!u) return '';
  if (u.player_id) return String(u.player_id);
  if (u.discord_id) return String(u.discord_id);
  return '';
};

/**
 * Collapsible right sidebar showing online players and direct messages.
 * On mobile (<768px): shows a FAB that opens a fullscreen overlay.
 */
export default function OnlineSidebar({
  onlineUsers,
  count,
  messages,
  sendMessage,
  activeChat,
  setActiveChat,
  closeChat,
  unreadCount,
  totalUnread,
  myPresenceKey,
}) {
  const { t } = useTranslation();
  const { user, isLoggedIn, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('sidebar_collapsed') === 'true'
  );
  const [activeTab, setActiveTab] = useState('online'); // 'online' | 'chat' | 'ai'
  const [chatInput, setChatInput] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [aiAvailable, setAiAvailable] = useState(false);
  const messagesEndRef = useRef(null);

  // Backend-gate för AI-fliken: dölj knapp/ikon helt om användaren saknar
  // chat.use-permission eller om backend ej är konfigurerad.
  useEffect(() => {
    if (!isLoggedIn) {
      setAiAvailable(false);
      return undefined;
    }
    let cancelled = false;
    fetch(`${API_URL}/api/chat/availability`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => { if (!cancelled) setAiAvailable(!!data.available); })
      .catch(() => { if (!cancelled) setAiAvailable(false); });
    return () => { cancelled = true; };
  }, [isLoggedIn, getAuthHeaders]);

  // Persist collapse state
  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', collapsed);
  }, [collapsed]);

  // Warm the shared country cache for everyone currently online so <SteamFlag>
  // can show each player's profile flag next to their name (resolved by steamid).
  useEffect(() => {
    const ids = onlineUsers.map((u) => u.steam_id).filter(Boolean);
    if (ids.length) fetchCountriesBulk(ids);
  }, [onlineUsers]);

  // Warm the shared avatar cache so <SteamAvatar> shows real Steam photos in the
  // online list + chat instead of the initial fallback. SteamAvatar re-renders
  // itself when a batch lands (via onAvatarsLoaded), so no version bump here.
  useEffect(() => {
    const ids = onlineUsers.map((u) => u.steam_id).filter(Boolean);
    if (ids.length) fetchAvatarsBulk(ids);
  }, [onlineUsers]);

  // Warm the shared ELO cache the same way so each row can show the player's
  // rank shield on the right (Esplay-style). Re-render when a batch lands.
  const [, setEloVersion] = useState(0);
  useEffect(() => {
    const ids = onlineUsers.map((u) => u.steam_id).filter(Boolean);
    if (ids.length) fetchEloBulk(ids);
  }, [onlineUsers]);
  useEffect(() => onEloLoaded(() => setEloVersion((v) => v + 1)), []);

  // Match draft (➕ on rows): re-render on changes + power the shortcut row.
  const [draft, setDraft] = useState(() => getMatchDraft());
  useEffect(() => subscribeMatchDraft(setDraft), []);

  // Auto-scroll chat to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, activeChat]);

  // Switch to chat tab when opening a chat
  useEffect(() => {
    if (activeChat) setActiveTab('chat');
  }, [activeChat]);

  const handleSend = () => {
    const text = chatInput.trim();
    if (!text || !activeChat) return;
    sendMessage(activeChat, text);
    setChatInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Find the active chat user's info
  const chatUser = useMemo(() => {
    if (!activeChat) return null;
    return onlineUsers.find(u => getKey(u) === activeChat) || { presence_key: activeChat, steam_username: activeChat };
  }, [activeChat, onlineUsers]);

  // Chat conversation list: users we have messages with, sorted by unread
  const conversations = useMemo(() => {
    const convIds = Object.keys(messages).filter(id => messages[id]?.length > 0);
    return convIds
      .map(id => {
        const userInfo = onlineUsers.find(u => getKey(u) === id);
        const lastMsg = messages[id][messages[id].length - 1];
        return {
          presence_key: id,
          name: userInfo?.steam_username || lastMsg?.from_name || id,
          steam_id: userInfo?.steam_id || '',
          steam_avatar: userInfo?.steam_avatar || '',
          lastMessage: lastMsg?.text || '',
          lastTime: lastMsg?.timestamp || '',
          unread: unreadCount[id] || 0,
          isOnline: !!userInfo,
        };
      })
      .sort((a, b) => (b.unread - a.unread) || (b.lastTime.localeCompare(a.lastTime)));
  }, [messages, onlineUsers, unreadCount]);

  const chatMessages = activeChat ? (messages[activeChat] || []) : [];

  // myPresenceKey kommer från usePresence (samma som backend WS-identify byggde).
  // Fallback för säkerhet om prop saknas (t.ex. äldre App.js-rendering).
  const myKey = myPresenceKey || (user ? (user.player_id ? String(user.player_id) : String(user.discord_id || '')) : '');

  // Filter out self from online list
  const otherUsers = onlineUsers.filter(u => getKey(u) !== myKey);
  const otherCount = otherUsers.length;

  /** Shared content rendered in both desktop sidebar and mobile overlay. */
  const renderContent = () => (
    <>
      {activeTab === 'online' && (
        <div className="sidebar-list">
          {otherUsers.length === 0 && (
            <p className="sidebar-empty">{t('sidebar.noOneOnline')}</p>
          )}
          {otherUsers.map(u => {
            const elo = u.steam_id ? eloCache[u.steam_id] : null;
            const tier = elo ? getRankTier(elo.elo) : null;
            const inDraft = u.discord_id ? isInMatchDraft(u.discord_id) : false;
            return (
              <div key={getKey(u)} className="player-row">
                <div
                  className="player-avatar-wrap"
                  onClick={() => u.steam_id && navigate(`/stats/players/${u.steam_id}`)}
                >
                  <SteamAvatar
                    steamid64={u.steam_id}
                    name={u.steam_username || u.discord_username}
                    size="small"
                  />
                  <span className={`status-dot ${u.status === 'in_match' ? 'in-match' : 'online'}`} />
                </div>
                <SteamFlag
                  steamid64={u.steam_id}
                  className="player-flag"
                  onClick={() => u.steam_id && navigate(`/stats/players/${u.steam_id}`)}
                />
                <span
                  className="player-name"
                  title={t('sidebar.viewProfile')}
                  onClick={() => u.steam_id && navigate(`/stats/players/${u.steam_id}`)}
                >
                  {u.steam_username || u.discord_username || getKey(u)}
                </span>
                {u.status === 'in_match' && <span className="in-match-label">{t('sidebar.inMatch')}</span>}
                <span className="player-actions">
                  {isLoggedIn && u.discord_id && (
                    <button
                      className={`player-add-btn ${inDraft ? 'added' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleMatchDraft({
                          discord_id: u.discord_id,
                          steam_id: u.steam_id || '',
                          name: u.steam_username || u.discord_username || '',
                        });
                      }}
                      title={inDraft ? t('sidebar.removeFromCreate') : t('sidebar.addToCreate')}
                      aria-label={inDraft ? t('sidebar.removeFromCreate') : t('sidebar.addToCreate')}
                    >
                      {inDraft ? '✓' : '+'}
                    </button>
                  )}
                  {/* Rank till höger; hover byter till chattikon (Esplay-stil) */}
                  <span
                    className="player-rank-slot"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveChat(getKey(u));
                    }}
                    title={tier ? `${tier.name} — ${elo.elo} ELO` : t('sidebar.chat')}
                  >
                    <span className="player-rank">
                      {tier ? (
                        <RankShield tier={tier} size={20} />
                      ) : (
                        <span className="player-rank-unranked">–</span>
                      )}
                    </span>
                    <span className="player-chat-icon" aria-label={t('sidebar.startChat')}>💬</span>
                  </span>
                </span>
              </div>
            );
          })}
          {draft.length > 0 && (
            <button
              className="draft-shortcut"
              onClick={() => navigate('/create')}
            >
              {t('sidebar.createShortcut', { count: draft.length })}
            </button>
          )}
        </div>
      )}

      {activeTab === 'chat' && !activeChat && (
        <div className="sidebar-list">
          {conversations.length === 0 && (
            <p className="sidebar-empty">{t('sidebar.noMessages')}</p>
          )}
          {conversations.map(c => (
            <div
              key={c.presence_key}
              className="conv-row"
              onClick={() => setActiveChat(c.presence_key)}
            >
              <div className="player-avatar-wrap">
                <SteamAvatar steamid64={c.steam_id} name={c.name} size="small" />
                {c.isOnline && <span className="status-dot online" />}
              </div>
              <SteamFlag steamid64={c.steam_id} className="player-flag" />
              <div className="conv-info">
                <span className="conv-name">{c.name}</span>
                <span className="conv-preview">{c.lastMessage.slice(0, 30)}</span>
              </div>
              {c.unread > 0 && <span className="conv-unread">{c.unread}</span>}
            </div>
          ))}
        </div>
      )}

      {activeTab === 'ai' && (
        <div className="sidebar-ai-tab">
          <AiChatPanel />
        </div>
      )}

      {activeTab === 'chat' && activeChat && (
        <div className="chat-view">
          <div className="chat-header">
            <button className="chat-back" onClick={closeChat}>←</button>
            <SteamAvatar
              steamid64={chatUser?.steam_id || ''}
              name={chatUser?.steam_username || ''}
              size="small"
            />
            <SteamFlag steamid64={chatUser?.steam_id} className="player-flag" />
            <span className="chat-username">{chatUser?.steam_username || chatUser?.presence_key}</span>
          </div>
          <div className="chat-messages">
            {chatMessages.map((msg, i) => (
              <div
                key={i}
                className={`chat-msg ${msg.from === myKey ? 'own' : 'other'}`}
              >
                {msg.from !== myKey && (
                  <span className="msg-sender">{msg.from_name}</span>
                )}
                <span className="msg-text">{msg.text}</span>
                <span className="msg-time">
                  {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString(currentLocale(), { hour: '2-digit', minute: '2-digit' }) : ''}
                </span>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          <div className="chat-input-wrap">
            <input
              type="text"
              className="chat-input"
              placeholder={t('sidebar.messagePlaceholder')}
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={handleKeyDown}
              maxLength={500}
            />
            <button className="chat-send" onClick={handleSend} disabled={!chatInput.trim()}>
              ➤
            </button>
          </div>
        </div>
      )}
    </>
  );

  /** Tabs bar shared between desktop and mobile. */
  const renderTabs = () => (
    <div className="sidebar-tabs">
      <button
        className={`sidebar-tab ${activeTab === 'online' ? 'active' : ''}`}
        onClick={() => { setActiveTab('online'); closeChat(); }}
      >
        {t('sidebar.onlineTab', { count: otherCount })}
      </button>
      <button
        className={`sidebar-tab ${activeTab === 'chat' ? 'active' : ''}`}
        onClick={() => setActiveTab('chat')}
      >
        {t('sidebar.chatTab')}
        {totalUnread > 0 && <span className="tab-unread">{totalUnread}</span>}
      </button>
      {aiAvailable && (
        <button
          className={`sidebar-tab ${activeTab === 'ai' ? 'active' : ''}`}
          onClick={() => { setActiveTab('ai'); closeChat(); }}
          title={t('sidebar.aiTab')}
        >
          🤖 AI
        </button>
      )}
    </div>
  );

  return (
    <>
      {/* ── Desktop sidebar ── */}
      {collapsed ? (
        <aside className="online-sidebar collapsed" aria-label="Online players">
          <button
            className="sidebar-collapsed-btn"
            onClick={() => { setCollapsed(false); setActiveTab('online'); closeChat(); }}
            title={t('sidebar.online')}
          >
            <span className="sidebar-icon">👥</span>
            {otherCount > 0 && <span className="sidebar-badge">{otherCount}</span>}
          </button>
          <button
            className="sidebar-collapsed-btn"
            onClick={() => { setCollapsed(false); setActiveTab('chat'); }}
            title={t('sidebar.chatTab')}
          >
            <span className="sidebar-icon">💬</span>
            {totalUnread > 0 && <span className="sidebar-unread-badge">{totalUnread}</span>}
          </button>
          {aiAvailable && (
            <button
              className="sidebar-collapsed-btn"
              onClick={() => { setCollapsed(false); setActiveTab('ai'); closeChat(); }}
              title={t('sidebar.aiTab')}
            >
              <span className="sidebar-icon">🤖</span>
            </button>
          )}
        </aside>
      ) : (
        <aside className="online-sidebar open" aria-label="Online players">
          <div className="sidebar-header">
            {renderTabs()}
            <button className="sidebar-toggle-close" onClick={() => setCollapsed(true)} title={t('sidebar.minimize')}>
              ✕
            </button>
          </div>
          {renderContent()}
        </aside>
      )}

      {/* ── Mobile FAB ── */}
      {!mobileOpen && (
        <button
          className="mobile-sidebar-fab"
          onClick={() => setMobileOpen(true)}
          aria-label={t('sidebar.showOnlineAndChat')}
        >
          👥
          {otherCount > 0 && <span className="mobile-fab-badge">{otherCount}</span>}
          {totalUnread > 0 && <span className="mobile-fab-unread">{totalUnread}</span>}
        </button>
      )}

      {/* ── Mobile fullscreen overlay ── */}
      {mobileOpen && (
        <div className="mobile-sidebar-overlay">
          <div className="mobile-overlay-header">
            {renderTabs()}
            <button
              className="mobile-overlay-close"
              onClick={() => { setMobileOpen(false); closeChat(); }}
            >
              ✕
            </button>
          </div>
          <div className="mobile-overlay-body">
            {renderContent()}
          </div>
        </div>
      )}
    </>
  );
}
